// Ambanji mock data — original copy, no third-party review reproductions

export const navItems = [
  { label: 'Ambanji', href: '/#features', badge: 'AI' },
  { label: 'How it works', href: '/#how' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'For Agencies', href: '/#agency' },
  { label: 'Free Heatmap', href: '/#audit' },
  { label: 'FAQ', href: '/#faq' },
];

export const heroStats = [
  { value: '5 min', label: 'Setup time' },
  { value: '169 pts', label: 'Per heatmap scan' },
  { value: '100%', label: 'White-hat SEO' },
  { value: '24 / 7', label: 'Profile watching' },
];

// Generic 'as mentioned in' — using neutral publication names (no copied logos)
export const mentionedIn = [
  'Local Search Weekly',
  'The Small Biz Playbook',
  'GBP Insider',
  'Maps Marketing Digest',
  'Agency Ops Report',
  'Search Signals Podcast',
];

export const threePillars = [
  {
    icon: 'Radar',
    tint: 'orange',
    title: 'Ambanji watches the map',
    body: "Every day, Ambanji scans the grid around your business, compares you to the profiles winning your keywords, and points at the exact gaps holding you back.",
  },
  {
    icon: 'PenLine',
    tint: 'indigo',
    title: 'Ambanji drafts the work',
    body: "Weekly posts, review replies, photo captions, keyword-rich descriptions — all written in your voice and lined up for a quick yes-or-no from you.",
  },
  {
    icon: 'Send',
    tint: 'emerald',
    title: 'You approve, Ambanji Relay publishes',
    body: "Everything you green-light is pushed by Ambanji Relay straight into your CRM's social planner and email automations. One click, out the door, on schedule.",
  },
];

export const featureGrid = [
  { icon: 'Eye', title: '24/7 profile monitoring', body: 'Ambanji notices photo removals, review dips, and hour changes before your customers do.' },
  { icon: 'Sparkles', title: 'AI-drafted GBP posts', body: 'Fresh weekly posts written around the keywords you actually want to rank for.' },
  { icon: 'MessageSquare', title: 'Review reply drafts', body: 'Polite, on-brand replies queued for every new review — you approve, they publish.' },
  { icon: 'Grid3x3', title: 'Local Falcon heatmaps', body: '5x5 to 9x9 grids show exactly where you rank around your neighborhood.' },
  { icon: 'Link2', title: 'Citation tracker', body: 'Watch your NAP consistency across the directories that actually move rankings.' },
  { icon: 'Camera', title: 'Photo optimization', body: 'Ambanji flags missing categories, blurry uploads, and photos that never got seen.' },
  { icon: 'Users', title: 'Competitor benchmarking', body: 'See what the top-3 profiles in your grid are doing every week — and mirror the wins.' },
  { icon: 'Mail', title: 'Weekly progress email', body: 'A short, plain-English summary of what moved, what regressed, and what to try next.' },
  { icon: 'CalendarClock', title: 'Social planner sync', body: "Approved posts drop into your CRM's social planner queue automatically via Ambanji Relay." },
  { icon: 'Zap', title: 'Email automation sync', body: 'Trigger nurture sequences in your CRM the moment a new lead comes in from your GBP.' },
  { icon: 'BellRing', title: 'Ranking alerts', body: 'Get a ping the moment a keyword slips out of the top-3 so you can react early.' },
  { icon: 'FileText', title: 'White-label PDF reports', body: 'One-click monthly reports you can email or hand to a client meeting.' },
];

export const pricingTiers = [
  {
    name: 'Solo',
    price: 79,
    tagline: 'One profile, all the essentials.',
    features: [
      '1 Google Business Profile',
      '3 tracked keywords',
      '5x5 heatmap grid',
      'Monthly heatmap refresh',
      'Monthly rank + AI-search check',
      'Post & review reply drafts',
      'Monthly profile audit',
      '1 team seat',
      'Email support',
    ],
    cta: 'Start with Solo',
    highlight: false,
  },
  {
    name: 'Growth',
    price: 249,
    tagline: 'Small businesses ready to move up the map.',
    features: [
      '5 profiles',
      '5 keywords per profile',
      '7x7 heatmap grid',
      'Weekly heatmap refresh',
      'Weekly rank + AI-search check',
      'Citation monitoring',
      '5 AI video posts / month',
      'White-label reports',
      'Client portal',
      '3 team seats',
    ],
    cta: 'Choose Growth',
    highlight: true,
    badge: 'Most popular',
  },
  {
    name: 'Agency',
    price: 549,
    tagline: 'Manage a book of local clients with room to grow.',
    features: [
      '15 profiles',
      '10 keywords per profile',
      '7x7 heatmap grid',
      'Weekly refresh + AI-search check',
      'Citation monitoring',
      '25 AI video posts / month',
      'White-label reports & client portal',
      'Custom portal domain',
      '10 team seats',
      'Priority support',
    ],
    cta: 'Choose Agency',
    highlight: false,
    badge: 'Best for agencies',
  },
  {
    name: 'Scale',
    price: 1099,
    tagline: 'For agencies running dozens of locations.',
    features: [
      '40 profiles',
      '15 keywords per profile',
      '9x9 heatmap grid',
      'Weekly refresh + AI-search check',
      'Citation monitoring',
      'Unlimited AI video posts',
      'White-label reports & portal',
      'Custom portal domain',
      'Unlimited team seats',
      'Priority support + onboarding call',
    ],
    cta: 'Choose Scale',
    highlight: false,
  },
];

export const comparisonRows = [
  { feature: 'Starting price', ambanji: '$79 / month', merchynt: '$99 / profile / month', diy: 'Your time' },
  { feature: '5 profiles cost', ambanji: '$249 total', merchynt: '$495 + $100 agency fee = $595', diy: 'Countless hours' },
  { feature: 'Heatmap grid', ambanji: 'Up to 9x9', merchynt: 'Add-on', diy: 'Manual checks' },
  { feature: 'AI post drafts', ambanji: 'Included on every plan', merchynt: 'Included', diy: 'You write them' },
  { feature: 'Pushes to your CRM', ambanji: 'Native, built-in via Relay', merchynt: 'Not offered', diy: 'Copy-paste' },
  { feature: 'Review reply drafts', ambanji: 'Every plan', merchynt: 'Add-on', diy: 'You write them' },
  { feature: 'White-label reports', ambanji: 'Growth and up', merchynt: 'Agency add-on', diy: '—' },
  { feature: 'Cancel anytime', ambanji: 'Yes, monthly', merchynt: 'Yes', diy: '—' },
];

export const faqs = [
  {
    q: 'Will Google penalize my profile for using Ambanji?',
    a: "No. Ambanji only performs white-hat actions that Google itself recommends in the Google Business Profile guidelines: fresh posts, complete information, honest review replies, and quality photos. Nothing is spun, spammed, or scraped.",
  },
  {
    q: 'Do I have to write anything myself?',
    a: "No. Ambanji drafts every post, reply, and description in your brand voice. You get an approval queue where each item is one click to publish, one click to skip, or a quick edit if you want to tweak the wording.",
  },
  {
    q: 'How is Ambanji different from Merchynt, BrightLocal, or Semrush Local?',
    a: "Two things. First, Ambanji is priced for the real world: five profiles are $249 with us versus roughly $595 with Merchynt. Second, we push approved work directly into your CRM's social planner and email automations through Ambanji Relay, so you are not juggling six tabs to actually get the work out the door.",
  },
  {
    q: 'Do I need to connect my own CRM account?',
    a: "Only if you are running Ambanji as a done-for-you service for clients. If you just want the audits, heatmaps, and AI drafts for your own business, you can use Ambanji standalone. Ambanji Relay is optional and switched on per-client.",
  },
  {
    q: 'How long does setup take?',
    a: "Most owners are up and running in about five minutes. You paste your Google Business Profile URL, pick three to fifteen keywords, and Ambanji handles the first scan and drafts within the hour.",
  },
  {
    q: 'Can I cancel anytime?',
    a: "Yes. Every plan is month-to-month with no lock-in. Cancel from your dashboard and you keep access through the end of the paid period.",
  },
  {
    q: 'Does Ambanji get access to my Google account?',
    a: "Ambanji connects through Google's official Business Profile OAuth flow. We never see your password, and you can revoke access from your Google account settings at any time. Ambanji only requests the permissions needed to post, reply, and read insights.",
  },
  {
    q: 'Does Ambanji work outside the United States?',
    a: "Yes. Ambanji works in every country Google Business Profile supports. Heatmaps are pulled through Local Falcon, which is global, and AI drafts can be generated in any major language.",
  },
  {
    q: 'What shows up in the ChatGPT and Gemini answers about my business?',
    a: "Ambanji keeps your business information consistent across the directories those AI models are trained on, and publishes the kind of structured, keyword-rich content those models pick up. It is not a magic bullet, but it meaningfully raises your odds of being cited.",
  },
];

export const footerLinks = {
  Product: ['How it works', 'Pricing', 'Free heatmap', 'For agencies', 'Changelog'],
  Company: ['About', 'Careers', 'Blog', 'Contact'],
  Resources: ['Help center', 'API docs', 'GBP guide', 'Webinars'],
  Legal: ['Privacy policy', 'Terms of service', 'Cookie policy', 'Do not sell my info'],
};
